import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { MessageSquare, Plus, Search, WifiOff } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useChat } from '@/lib/chat-provider';
import {
  conversationTitle,
  formatListTime,
  messagePreview,
  otherMemberIds,
} from '@/lib/chat-model';
import { errorMessage } from '@/lib/errors';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NewGroupDialog } from './NewGroupDialog';
import { useDirectory } from './PeoplePicker';

export function ConversationList({ activeId }: { activeId: string | null }): React.ReactElement {
  const navigate = useNavigate();
  const { meId, online, typing, conversations, conversationsLoading, connected, enabled } =
    useChat();
  const [query, setQuery] = React.useState('');
  const [groupOpen, setGroupOpen] = React.useState(false);
  const needle = query.trim().toLowerCase();
  const directory = useDirectory(query, needle.length > 0);

  const openDirect = useMutation({
    mutationFn: (personId: string) => api.chatOpenDirect(personId),
    onSuccess: async ({ id }) => {
      setQuery('');
      await navigate({ to: '/chat/$conversationId', params: { conversationId: id } });
    },
  });

  const list = conversations ?? [];
  const shown = needle
    ? list.filter((c) => conversationTitle(c, meId).toLowerCase().includes(needle))
    : list;
  const directIds = new Set(
    list.filter((c) => c.kind === 'direct').flatMap((c) => otherMemberIds(c, meId)),
  );
  const people = needle
    ? (directory.data ?? []).filter((p) => p.id !== meId && !directIds.has(p.id))
    : [];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-col gap-3 border-b border-[hsl(var(--border))]/70 px-4 pb-3 pt-4">
        <div className="flex items-center justify-between gap-2">
          <h1 className="font-display text-2xl font-semibold tracking-tight">Chat</h1>
          <Button size="sm" variant="outline" onClick={() => setGroupOpen(true)}>
            <Plus />
            New group
          </Button>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats or people"
            aria-label="Search chats or people"
            className="h-10 pl-10"
          />
        </div>
        {enabled && !connected ? (
          <p className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
            <WifiOff className="size-3.5" />
            Reconnecting…
          </p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {conversationsLoading ? (
          <p className="px-4 py-6 text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
        ) : shown.length === 0 && people.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <div className="rounded-full bg-[hsl(var(--muted))] p-4 text-[hsl(var(--brand))]">
              <MessageSquare className="size-6" />
            </div>
            <p className="font-medium">{needle ? 'Nothing matches.' : 'No conversations yet.'}</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {needle
                ? 'Try another name.'
                : 'Search for a colleague above to start a direct chat, or create a group.'}
            </p>
          </div>
        ) : (
          <>
            {shown.length > 0 ? (
              <ul aria-label="Conversations" className="py-2">
                {shown.map((c) => {
                  const title = conversationTitle(c, meId);
                  const others = otherMemberIds(c, meId);
                  const isOnline = c.kind === 'direct' ? others.some((id) => online.has(id)) : undefined;
                  const typingHere = typing[c.id];
                  const active = c.id === activeId;
                  return (
                    <li key={c.id}>
                      <Link
                        to="/chat/$conversationId"
                        params={{ conversationId: c.id }}
                        className={cn(
                          'mx-2 flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition-colors',
                          active
                            ? 'bg-[hsl(var(--accent))]'
                            : 'hover:bg-[hsl(var(--muted))]',
                        )}
                      >
                        <Avatar
                          name={title}
                          personId={c.kind === 'direct' ? others[0] : undefined}
                          group={c.kind === 'group'}
                          online={isOnline}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline justify-between gap-2">
                            <span
                              className={cn(
                                'truncate text-sm',
                                c.unreadCount > 0 ? 'font-semibold' : 'font-medium',
                              )}
                            >
                              {title}
                            </span>
                            {c.lastMessage ? (
                              <span className="shrink-0 text-[11px] text-[hsl(var(--muted-foreground))]">
                                {formatListTime(c.lastMessage.createdAt)}
                              </span>
                            ) : null}
                          </span>
                          <span className="flex items-center justify-between gap-2">
                            <span
                              className={cn(
                                'truncate text-xs',
                                c.unreadCount > 0
                                  ? 'text-[hsl(var(--foreground))]'
                                  : 'text-[hsl(var(--muted-foreground))]',
                                c.lastMessage?.kind === 'system' || c.lastMessage?.redacted
                                  ? 'italic'
                                  : '',
                              )}
                            >
                              {typingHere && typingHere.size > 0
                                ? 'typing…'
                                : messagePreview(c, meId)}
                            </span>
                            {c.status === 'archived' ? (
                              <Badge variant="muted" className="shrink-0">
                                Archived
                              </Badge>
                            ) : c.unreadCount > 0 ? (
                              <Badge className="shrink-0 px-2">{c.unreadCount}</Badge>
                            ) : null}
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {people.length > 0 ? (
              <div className="border-t border-[hsl(var(--border))]/70 py-2">
                <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  People
                </p>
                <ul aria-label="People">
                  {people.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => openDirect.mutate(p.id)}
                        disabled={openDirect.isPending}
                        className="mx-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors hover:bg-[hsl(var(--muted))] disabled:opacity-60"
                      >
                        <Avatar name={p.name} personId={p.id} online={p.online} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{p.name}</span>
                          <span className="block truncate text-xs text-[hsl(var(--muted-foreground))]">
                            {p.employeeId ? `${p.employeeId} · ` : ''}
                            {p.email}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {openDirect.isError ? (
              <p className="px-4 py-2 text-xs text-[hsl(var(--destructive))]">
                {errorMessage(openDirect.error)}
              </p>
            ) : null}
          </>
        )}
      </div>
      <NewGroupDialog open={groupOpen} onOpenChange={setGroupOpen} />
    </div>
  );
}
